import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const queryClient = useQueryClient();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalMessages: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    pendingProjects: 0
  });

  // Récupérer les statistiques admin
  const { data: adminStats, isLoading } = useQuery(
    'admin-stats',
    async () => {
      const response = await axios.get('/api/admin/stats');
      return response.data;
    },
  );

  useEffect(() => {
    if (adminStats) {
      setStats(adminStats);
    }
  }, [adminStats]);

  const statCards = [
    {
      name: 'Utilisateurs',
      value: stats.totalUsers,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'positive'
    },
    {
      name: 'Projets',
      value: stats.totalProjects,
      icon: DocumentTextIcon,
      color: 'bg-green-500',
      change: '+8%',
      changeType: 'positive'
    },
    {
      name: 'Messages',
      value: stats.totalMessages,
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-purple-500',
      change: '+15%',
      changeType: 'positive'
    },
    {
      name: 'Revenus (€)',
      value: stats.totalRevenue,
      icon: CurrencyDollarIcon,
      color: 'bg-yellow-500',
      change: '+23%',
      changeType: 'positive'
    },
    {
      name: 'Abonnements Actifs',
      value: stats.activeSubscriptions,
      icon: CheckCircleIcon,
      color: 'bg-indigo-500',
      change: '+5%',
      changeType: 'positive'
    },
    {
      name: 'Projets en Attente',
      value: stats.pendingProjects,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
      change: '-2%',
      changeType: 'negative'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Tableau de Bord Admin</h1>
            <p className="text-gray-600 mt-1">Vue d'ensemble de la plateforme Bourse d'Idées</p>
          </div>
          <button
            onClick={() => queryClient.invalidateQueries('admin-stats')}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center text-sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-text">{stat.value.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <p className="text-xs text-gray-500">vs mois dernier</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Graphiques et Activités Récentes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique des utilisateurs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-text mb-4">Évolution des Utilisateurs</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">Graphique en cours de développement</p>
            </div>
          </div>
        </div>

        {/* Activités récentes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-text mb-4">Activités Récentes</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserGroupIcon className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-text">Nouvel utilisateur inscrit</p>
                <p className="text-xs text-gray-500">Jean Dupont s'est inscrit il y a 5 minutes</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-text">Nouveau projet soumis</p>
                <p className="text-xs text-gray-500">"App Mobile de Livraison" par Marie Martin</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ChatBubbleLeftRightIcon className="h-4 w-4 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-text">Nouvelle conversation</p>
                <p className="text-xs text-gray-500">Entrepreneur et investisseur en discussion</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CurrencyDollarIcon className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-text">Nouvel abonnement</p>
                <p className="text-xs text-gray-500">Plan Premium souscrit par Pierre Durand</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Rapides */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-text mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3" />
            <div className="text-left">
              <p className="font-medium text-text">Gérer les Utilisateurs</p>
              <p className="text-sm text-gray-600">Voir et modifier les comptes</p>
            </div>
          </button>
          
          <button className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <DocumentTextIcon className="h-6 w-6 text-green-600 mr-3" />
            <div className="text-left">
              <p className="font-medium text-text">Modérer les Projets</p>
              <p className="text-sm text-gray-600">Approuver ou rejeter</p>
            </div>
          </button>
          
          <button className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600 mr-3" />
            <div className="text-left">
              <p className="font-medium text-text">Support Client</p>
              <p className="text-sm text-gray-600">Messages et tickets</p>
            </div>
          </button>
          
          <button className="flex items-center p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors">
            <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 mr-3" />
            <div className="text-left">
              <p className="font-medium text-text">Gestion Financière</p>
              <p className="text-sm text-gray-600">Revenus et abonnements</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

